import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { BarChart3, ChevronDown, Download, MoreHorizontal, Plus } from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { PaginationControls, usePagination, paginateData } from '@/shared/components/pagination';
import type { RFIFilters, RFISortState, RFISummary } from '../types';
import { RFI_PRIORITY_LABELS, RFI_STATUS_LABELS } from '../types';
import { useRFI, useRFIs, useRFIMutations, useRFIStatusSummary } from '../hooks';
import { RfiFormDialog } from './RfiFormDialog';
import { RfisTable } from './RfisTable';
import { RfisFilters } from './RfisFilters';
import { RfiWidgets } from './RfiWidgets';
// Importing for the side effects: ``registerTour`` runs at module load
// for each of the RFIs tour variants.
import { RFIS_LIST_TOUR_ID } from '@/features/tours/rfis/list';
import { RFIS_COMBINED_TOUR_ID } from '@/features/tours/rfis/combined';
import '@/features/tours/rfis/detail';
import { useTour } from '@/features/tours';
import { sortListItemsByRules } from '@/shared/utils/listSorting';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';

const exportRfisToCsv = (items: RFISummary[]) => {
  const headers = [
    'RFI #',
    'Title',
    'Status',
    'Priority',
    'Responsible Party',
    'Assignee',
    'Due Date',
    'Drawing Ref',
    'Created',
  ];
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const rows = items.map((item) =>
    [
      item.rfi_number,
      item.title,
      RFI_STATUS_LABELS[item.status],
      RFI_PRIORITY_LABELS[item.priority],
      item.responsible_party_user
        ? `${item.responsible_party_user.first_name} ${item.responsible_party_user.last_name}`
        : '',
      item.assignee_user ? `${item.assignee_user.first_name} ${item.assignee_user.last_name}` : '',
      item.due_date ?? '',
      item.drawing_reference?.label ?? '',
      item.created_at,
    ]
      .map((cell) => escape(String(cell)))
      .join(',')
  );
  const csv = [headers.map(escape).join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rfis-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const RfisPage: React.FC = () => {
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [filters, setFilters] = useState<RFIFilters>({});
  const [sort, setSort] = useState<RFISortState[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | undefined>();
  const [summaryExpandedOverride, setSummaryExpandedOverride] = useState<boolean | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  // The same React-Query call ``RfisTable`` makes is reused here; deduped.
  const { data: items, isLoading, error } = useRFIs(numericProjectId, filters);
  const { data: editingItem } = useRFI(editingItemId, numericProjectId);
  const { data: statusSummary } = useRFIStatusSummary(numericProjectId);
  const { deleteItem } = useRFIMutations(numericProjectId);

  const allItems = useMemo(() => items ?? [], [items]);
  const sortedItems = useMemo(() => sortRfiItems(allItems, sort), [allItems, sort]);
  const totalCount = useMemo(
    () => (statusSummary ?? []).reduce((sum, entry) => sum + entry.count, 0),
    [statusSummary]
  );
  const filteredCount = allItems.length;

  const [paginationState, paginationActions, paginationInfo] = usePagination(filteredCount, 10);
  const pagedItems = useMemo(
    () => paginateData(sortedItems, paginationState.currentPage, paginationState.itemsPerPage),
    [sortedItems, paginationState.currentPage, paginationState.itemsPerPage]
  );

  const handleFiltersChange = (next: RFIFilters) => {
    setFilters(next);
    paginationActions.setCurrentPage(1);
  };

  const handleSortChange = (next: RFISortState[]) => {
    setSort(next);
    paginationActions.setCurrentPage(1);
  };

  // Auto-start the appropriate RFIs tour:
  //   - items exist → start the *combined* tour (list → click row → detail)
  //   - no items yet → start the list-only tour to introduce the feature
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  const hasItems = allItems.length >= 1;
  const isSummaryVisible = summaryExpandedOverride ?? !isMobileViewport;
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (hasItems) {
      if (hasSeen(RFIS_COMBINED_TOUR_ID)) return;
      if (hasSeen(RFIS_LIST_TOUR_ID)) return;
      void startTour(RFIS_COMBINED_TOUR_ID);
    } else {
      if (hasSeen(RFIS_LIST_TOUR_ID)) return;
      void startTour(RFIS_LIST_TOUR_ID);
    }
  }, [tourHydrated, active, startTour, hasSeen, hasItems]);

  const handleRowClick = (item: RFISummary) => {
    navigate(item.id);
  };

  const handleDelete = async (item: RFISummary) => {
    if (!window.confirm(`Delete ${item.rfi_number} “${item.title}”? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync(item.id);
      toast({ title: 'RFI deleted' });
    } catch (deleteError) {
      toast({
        title: 'Unable to delete RFI',
        description: handleApiError(deleteError),
        variant: 'destructive',
      });
    }
  };

  return (
    <BaseLayout>
      <div className="min-w-0 space-y-6 p-4 md:p-6">
        {/* Page header */}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">RFIs</h1>
            <p className="mt-1 text-sm text-muted-foreground">Track questions and responses.</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-3 mt-1 h-8 gap-2 px-3 text-muted-foreground hover:text-foreground"
              aria-expanded={isSummaryVisible}
              aria-controls="rfis-summary"
              data-tour-id="rfis-summary-toggle"
              onClick={() =>
                setSummaryExpandedOverride((current) => !(current ?? !isMobileViewport))
              }
            >
              <BarChart3 className="h-4 w-4" />
              {isSummaryVisible ? 'Hide summary' : 'Show summary'}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isSummaryVisible ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>
          <div
            className={`grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center ${
              isSummaryVisible ? 'order-2 sm:order-none' : ''
            }`}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={filteredCount === 0}
                >
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 max-w-[calc(100vw-2rem)]">
                <DropdownMenuItem onSelect={() => exportRfisToCsv(allItems)}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => setIsCreateOpen(true)}
              disabled={!numericProjectId}
              data-tour-id="rfis-new"
            >
              <Plus className="mr-2 h-4 w-4" />
              New RFI
            </Button>
          </div>

          {isSummaryVisible && (
            <div id="rfis-summary" className="order-1 min-w-0 sm:order-none sm:col-span-2 sm:mt-3">
              <RfiWidgets projectId={numericProjectId} />
            </div>
          )}
        </div>

        <RfisFilters
          projectId={numericProjectId}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          totalCount={totalCount}
          filteredCount={filteredCount}
          sort={sort}
          onSortChange={handleSortChange}
        />

        <RfisTable
          items={pagedItems}
          isLoading={isLoading}
          error={error}
          onRowClick={handleRowClick}
          onEdit={(item) => setEditingItemId(item.id)}
          onDelete={(item) => void handleDelete(item)}
        />

        {!isLoading && !error && filteredCount > 0 && (
          <PaginationControls
            state={paginationState}
            actions={paginationActions}
            info={paginationInfo}
          />
        )}
      </div>

      <RfiFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        projectId={numericProjectId}
      />
      <RfiFormDialog
        open={!!editingItemId && !!editingItem}
        onClose={() => setEditingItemId(undefined)}
        projectId={numericProjectId}
        item={editingItem}
      />
    </BaseLayout>
  );
};

const RFI_PRIORITY_ORDER = { LOW: 0, NORMAL: 1, HIGH: 2, URGENT: 3 } as const;

export const sortRfiItems = (items: RFISummary[], sort: readonly RFISortState[]) => {
  return sortListItemsByRules(items, sort, (item, key) => {
    switch (key) {
      case 'rfi_number':
        return item.rfi_number;
      case 'status':
        return RFI_STATUS_LABELS[item.status];
      case 'priority':
        return RFI_PRIORITY_ORDER[item.priority];
      case 'responsible_party':
        return item.responsible_party_user
          ? `${item.responsible_party_user.first_name} ${item.responsible_party_user.last_name}`
          : null;
      case 'due_date':
        return item.due_date ? Date.parse(item.due_date) : null;
      case 'created_at':
        return Date.parse(item.created_at);
    }
  });
};
